import React from 'react';
import { Link } from 'react-router-dom';

function WorkbookMain() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-cream-50 to-white">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div>
              <div className="inline-block px-4 py-2 bg-cream-100 rounded-full mb-6">
                <span className="text-cream-700 text-sm font-medium">Self-Paced Digital Workbook</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-light text-charcoal mb-6">
                Boundary Reset Workbook
              </h1>
              
              <p className="text-2xl text-charcoal/70 mb-6 leading-relaxed">
                Stop Overcommitting. Start Feeling Confident.
              </p>
              
              <p className="text-lg text-charcoal/70 mb-8 leading-relaxed">
                A guided workbook with practical tools to identify weak boundaries, overcome guilt, and build lasting confidence in saying no.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link 
                  to="/workbook/get-started"
                  className="inline-block bg-cream-600 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-cream-700 transition-all duration-300 shadow-lg hover:shadow-xl text-center"
                >
                  Get the Workbook - $29
                </Link>
                <Link 
                  to="/workbook/preview"
                  className="inline-block bg-white text-cream-700 border-2 border-cream-600 px-8 py-4 rounded-full text-lg font-medium hover:bg-cream-50 transition-all duration-300 text-center"
                >
                  Preview Inside
                </Link>
              </div>
              
              {/* Trust Signals */}
              <div className="flex items-center gap-6 text-sm text-charcoal/60">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-cream-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Instant Download</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-cream-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Lifetime Access</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-cream-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Free Updates</span>
                </div>
              </div>
            </div>
            
            {/* Right Column - Workbook Image */}
            <div className="relative">
              <img 
                src="https://images.squarespace-cdn.com/content/v1/5e7be0f4a4a1a06a00b2ccbf/25730e1c-0fbf-4d2a-b168-ef8f151812d5/Original+CCM+Workbook+Template.png"
                alt="Boundary Reset Workbook Preview"
                className="w-full rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-cream-600 text-white px-6 py-3 rounded-full shadow-lg">
                <span className="text-2xl font-semibold">$29</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Get Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light text-charcoal text-center mb-16">
            What's Inside the Workbook
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-cream-50 p-8 rounded-2xl">
              <div className="w-12 h-12 bg-cream-600 rounded-full flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-medium text-charcoal mb-4">Self-Assessment</h3>
              <p className="text-charcoal/70 leading-relaxed">
                Identify where your boundaries are weak and understand the patterns that keep you stuck in people-pleasing mode.
              </p>
            </div>
            
            <div className="bg-cream-50 p-8 rounded-2xl">
              <div className="w-12 h-12 bg-cream-600 rounded-full flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-2xl font-medium text-charcoal mb-4">Practical Exercises</h3>
              <p className="text-charcoal/70 leading-relaxed">
                Work through guided exercises to practice setting boundaries, saying no, and overcoming guilt in real-life scenarios.
              </p>
            </div>
            
            <div className="bg-cream-50 p-8 rounded-2xl">
              <div className="w-12 h-12 bg-cream-600 rounded-full flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-medium text-charcoal mb-4">Progress Tracking</h3>
              <p className="text-charcoal/70 leading-relaxed">
                Track your boundary-setting wins, monitor your progress, and celebrate how far you've come on your journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="py-20 px-6 bg-cream-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light text-charcoal text-center mb-12">
            Is This Workbook For You?
          </h2>
          
          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-lg">
            <p className="text-lg text-charcoal/80 mb-6">
              This workbook is perfect if you:
            </p>
            
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <svg className="w-6 h-6 text-cream-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-charcoal/80">Want a self-paced resource to work on boundaries independently</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-6 h-6 text-cream-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-charcoal/80">Need practical exercises and scripts to practice saying no</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-6 h-6 text-cream-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-charcoal/80">Want a more affordable option before committing to therapy</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-6 h-6 text-cream-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-charcoal/80">Are already in therapy and want supplemental resources</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-6 h-6 text-cream-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-charcoal/80">Prefer working through things at your own pace on your own schedule</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light text-charcoal text-center mb-16">
            What People Are Saying
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-cream-50 p-8 rounded-2xl">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-cream-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-charcoal/80 italic mb-4">
                "This workbook helped me see exactly where I was giving away my power. The exercises were practical and I actually used them!"
              </p>
              <p className="text-charcoal/60 text-sm">- Sarah M.</p>
            </div>
            
            <div className="bg-cream-50 p-8 rounded-2xl">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-cream-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-charcoal/80 italic mb-4">
                "I loved working through this at my own pace. The scripts for saying no were game-changers for me."
              </p>
              <p className="text-charcoal/60 text-sm">- Jennifer K.</p>
            </div>
            
            <div className="bg-cream-50 p-8 rounded-2xl">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-cream-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-charcoal/80 italic mb-4">
                "Best $29 I've spent! This gave me the confidence to finally start setting boundaries without feeling guilty."
              </p>
              <p className="text-charcoal/60 text-sm">- Lisa R.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-cream-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light text-charcoal text-center mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl">
              <h3 className="text-xl font-medium text-charcoal mb-2">How do I access the workbook?</h3>
              <p className="text-charcoal/80">
                After purchase, you'll receive instant access to download the PDF workbook. You can print it or fill it out digitally—whatever works best for you!
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl">
              <h3 className="text-xl font-medium text-charcoal mb-2">Is this a replacement for therapy?</h3>
              <p className="text-charcoal/80">
                No, the workbook is a self-help resource and not a substitute for professional therapy. It's perfect as a standalone tool or to supplement therapy sessions.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl">
              <h3 className="text-xl font-medium text-charcoal mb-2">How long does it take to complete?</h3>
              <p className="text-charcoal/80">
                Most people work through it over 2-4 weeks, but it's self-paced! You can take as much time as you need. There's no rush or deadline.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl">
              <h3 className="text-xl font-medium text-charcoal mb-2">Do I get updates?</h3>
              <p className="text-charcoal/80">
                Yes! If I update the workbook, you'll receive the new version for free. Your purchase includes lifetime access to all future updates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-cream-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light text-charcoal mb-6">
            Ready to Reset Your Boundaries?
          </h2>
          <p className="text-xl text-charcoal/70 mb-8">
            Start your journey to confident, guilt-free boundary-setting today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link 
              to="/workbook/get-started"
              className="inline-block bg-cream-600 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-cream-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Get Instant Access - $29
            </Link>
            <Link 
              to="/workbook/preview"
              className="inline-block bg-white text-cream-700 border-2 border-cream-600 px-8 py-4 rounded-full text-lg font-medium hover:bg-cream-50 transition-all duration-300"
            >
              Preview Sample Pages
            </Link>
          </div>
          <p className="text-sm text-charcoal/60">
            One-time payment • Instant download • Lifetime access • Free updates
          </p>
        </div>
      </section>
    </div>
  );
}

export default WorkbookMain;
